import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

// Unified search lives on its own isolated stack (search.app.lxop.in). Defaults to it;
// override with REACT_APP_SEARCH_API_URL. Falls back to the main API if blank.
const SEARCH_BASE = (process.env.REACT_APP_SEARCH_API_URL || 'https://search.app.lxop.in').replace(/\/$/, '');

const CATEGORIES = [
  { key: 'articles',   label: 'Articles',        emoji: '📰' },
  { key: 'labs',       label: 'Labs',            emoji: '🧪' },
  { key: 'kubernetes', label: 'Kubernetes Labs', emoji: '☸️' },
  { key: 'jobs',       label: 'Jobs',            emoji: '💼' },
  { key: 'social',     label: 'Community',       emoji: '✨' },
];

function SearchIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 115 11a6 6 0 0112 0z"/>
    </svg>
  );
}

function ResultItem({ item, onSelect }) {
  return (
    <button
      onClick={() => onSelect(item)}
      className="w-full flex items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-white/[0.06] group"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-200 truncate group-hover:text-white transition-colors">
          {item.title}
        </p>
        {item.meta && (
          <p className="text-xs text-gray-600 mt-0.5 truncate">{item.meta}</p>
        )}
        {item.excerpt && (
          <p className="text-xs text-gray-600 mt-0.5 line-clamp-1">{item.excerpt}</p>
        )}
      </div>
      <svg className="w-3.5 h-3.5 text-gray-700 group-hover:text-gray-400 flex-shrink-0 mt-0.5 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7"/>
      </svg>
    </button>
  );
}

export default function SearchModal({ open, onClose }) {
  const navigate   = useNavigate();
  const inputRef   = useRef(null);
  const debounceRef = useRef(null);

  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  // Focus input when modal opens
  useEffect(() => {
    if (open) {
      setQuery('');
      setResults(null);
      setError(null);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open]);

  // Debounced search
  const search = useCallback((q) => {
    clearTimeout(debounceRef.current);
    if (q.length < 2) { setResults(null); setLoading(false); return; }
    setLoading(true);
    setError(null);
    debounceRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get('/search', { baseURL: SEARCH_BASE, params: { q } });
        setResults(data.results);
      } catch {
        setError('Search failed. Please try again.');
      } finally {
        setLoading(false);
      }
    }, 300);
  }, []);

  useEffect(() => {
    search(query);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  // Close on ESC
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const handleSelect = (item) => {
    onClose();
    if (item.external) {
      window.open(item.url, '_blank', 'noopener,noreferrer');
    } else {
      navigate(item.url);
    }
  };

  const totalResults = results
    ? CATEGORIES.reduce((sum, c) => sum + (results[c.key]?.length || 0), 0)
    : 0;

  const hasResults = totalResults > 0;

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-16 px-4"
      style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(6px)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-xl rounded-2xl overflow-hidden shadow-2xl shadow-black/50"
        style={{ backgroundColor: 'var(--lx-surface)', border: '1px solid var(--lx-border)' }}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3.5" style={{ borderBottom: '1px solid var(--lx-border)' }}>
          {loading
            ? <div className="w-4 h-4 border-2 border-indigo-400 border-t-transparent rounded-full animate-spin flex-shrink-0" />
            : <SearchIcon className="w-4 h-4 text-gray-500 flex-shrink-0" />
          }
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search articles, labs, jobs, community posts…"
            className="flex-1 bg-transparent text-sm text-white placeholder-gray-600 outline-none"
          />
          {query && (
            <button onClick={() => setQuery('')}
              className="text-gray-600 hover:text-gray-400 transition-colors flex-shrink-0 text-lg leading-none">
              ×
            </button>
          )}
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded text-[10px] text-gray-600 border border-gray-700 font-mono">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto">
          {/* Empty / initial state */}
          {!query && !results && (
            <div className="px-4 py-8 text-center">
              <SearchIcon className="w-8 h-8 text-gray-700 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Search across articles, labs, jobs and community posts</p>
              <p className="text-xs text-gray-700 mt-1">Type at least 2 characters</p>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="px-4 py-6 text-center">
              <p className="text-sm text-red-400">{error}</p>
            </div>
          )}

          {/* No results */}
          {!loading && results && !hasResults && (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-gray-500">No results for <span className="text-white">"{query}"</span></p>
              <p className="text-xs text-gray-700 mt-1">Try a different keyword</p>
            </div>
          )}

          {/* Results grouped by category */}
          {results && hasResults && CATEGORIES.map((cat) => {
            const items = results[cat.key] || [];
            if (items.length === 0) return null;
            return (
              <div key={cat.key}>
                <div className="px-4 py-2 flex items-center justify-between" style={{ borderBottom: '1px solid var(--lx-border)' }}>
                  <p className="text-[10px] font-semibold text-gray-600 uppercase tracking-wider">
                    {cat.emoji} {cat.label}
                  </p>
                  {cat.key !== 'jobs' && (
                    <button
                      onClick={() => {
                        onClose();
                        const paths = { articles: `/posts?q=${encodeURIComponent(query)}`, labs: '/labs', kubernetes: '/labs/kubernetes', social: `/social/feed` };
                        if (paths[cat.key]) navigate(paths[cat.key]);
                      }}
                      className="text-[10px] text-indigo-400 hover:text-indigo-300 transition-colors"
                    >
                      View all →
                    </button>
                  )}
                </div>
                {items.map((item) => (
                  <ResultItem key={item.id} item={item} onSelect={handleSelect} />
                ))}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 flex items-center justify-between" style={{ borderTop: '1px solid var(--lx-border)' }}>
          <p className="text-[10px] text-gray-700">
            {results && hasResults ? `${totalResults} result${totalResults !== 1 ? 's' : ''}` : 'LearnXops Search'}
          </p>
          <div className="flex items-center gap-3 text-[10px] text-gray-700">
            <span><kbd className="font-mono">↑↓</kbd> navigate</span>
            <span><kbd className="font-mono">↵</kbd> open</span>
          </div>
        </div>
      </div>
    </div>
  );
}
